import React, { FC, useEffect, useState } from 'react'
import cx from 'classnames'
import { Menu, Dropdown, Tabs, Button, Row, Col, message, Tooltip, Popover, Spin } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { MenuProps } from 'antd/lib'

// hooks
import useBackUrl from '../../hooks/useBackUrl'

// utils
import { postReq } from '../../utils/request'
import { ANT_MESSAGE_KEY, EDITING_TIME_MINUTES_LIMIT, FORM_BUILDER_HEADER_TABS, FORM_TEMPLATE_STATUSES, PAGE, ROLES } from '../../utils/enums'

// reducers
import { RootState } from '../../reducers'
import { logOutUser } from '../../reducers/authorization/authActions'
import { FORM_EDIT_CHANGE, FORM_TEMPLATE, FORM_EDITED, RESET_FORM_STORE } from '../../reducers/form/formTypes'

// assets
import { ReactComponent as ChevronDownIcon } from '../../assets/icons/BaseMiniIcons/chevron-down-3-icon.svg'
import { ReactComponent as LogOutIcon } from '../../assets/icons/BaseIcons/logout-1-icon.svg'
import { ReactComponent as ArrowLeftIcon } from '../../assets/icons/BaseIcons/arrow-left-1-icon.svg'
import { ReactComponent as ExclamationIcon } from '../../assets/icons/BaseIcons/exclamation-circle-1-icon.svg'
import { ReactComponent as AdminAvatar } from '../../assets/images/paradigma-admin-logo.svg'
import { ReactComponent as CopyLinkIcon } from '../../assets/icons/BaseIcons/copy-icon.svg'

const { TabPane } = Tabs

type Props = {
	page?: PAGE
}

const FormBuilderHeader: FC<Props> = (props) => {
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const data = useSelector((state: RootState) => state.formTemplate)
	const id = data.formTemplate.data?.id
	const loginUserData = useSelector((state: RootState) => state.auth)
	const [activeKey, setActiveKey] = useState<string>(props.page || '')
	const [status, setStatus] = useState<string>('')
	const disable = useSelector((state: RootState) => state.formTemplate.disable)
	const load = useSelector((state: RootState) => state.formTemplate.load)
	const [processing, setProcessing] = useState(false)
	const [backUrlLink] = useBackUrl()
	const backUrlQuery = window.location.search
	const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />
	const BEVersion = useSelector((state: RootState) => state.versions.versions)
	const iFrameVersion = useSelector((state: RootState) => state.versions.iframeVersions)

	// check if user something change in from items
	useEffect(() => {
		if (data.formEdited) {
			if (data.formTemplate.data?.backup.status !== FORM_TEMPLATE_STATUSES.ARCHIVED) {
				// update form template backup status when user edit something in form items
				dispatch({ type: FORM_TEMPLATE.FORM_TEMPLATE_CHANGE_BACKUP_STATUS, status: FORM_TEMPLATE_STATUSES.IN_PROGRESS })
				setStatus(FORM_TEMPLATE_STATUSES.IN_PROGRESS)
			}
			const edited = false
			dispatch({ type: FORM_EDITED, edited })
		}
	}, [dispatch, data.formEdited, data.formTemplate])

	useEffect(() => {
		// check if from is edited
		const time = Math.abs(dayjs(data.formTemplate.data?.updatedAt || 0).diff(dayjs(), 'minutes'))
		// check editor id or latest updated time
		if (
			data.formTemplate.data?.backup?.editor?.id === loginUserData.authUser.data?.id ||
			time >= EDITING_TIME_MINUTES_LIMIT ||
			data.formTemplate.data?.backup?.editor === null
		) {
			const disabled = false
			dispatch({ type: FORM_EDIT_CHANGE, disabled })
			message.destroy(ANT_MESSAGE_KEY)
		} else if (data.formTemplate.data?.backup?.editor !== null) {
			// if admin editing form show message to another admins
			const editorName = data.formTemplate.data?.editor?.fullName
			const disabled = true
			dispatch({ type: FORM_EDIT_CHANGE, disabled })
			message.destroy(ANT_MESSAGE_KEY)
			message.config({
				duration: 10000,
				maxCount: 1
			})
			message.info({
				icon: <ExclamationIcon width={20} height={20} />,
				content: t(`loc:This form is currently edited by ${editorName}.`),
				key: ANT_MESSAGE_KEY
			})
		}
		setStatus(data.formTemplate.data?.backup?.status || '')
	}, [data.formTemplate, dispatch, t, loginUserData.authUser])

	// publish form changes
	const publishForm = async () => {
		try {
			if (id) {
				setProcessing(true)
				const formId = parseInt(id, 10)
				await postReq(`/api/admin/forms/${formId}/publish`, undefined)
			}
		} catch (err: any) {
			/* eslint-disable-next-line no-console */
			console.error(err.message)
		} finally {
			setProcessing(false)
		}
	}

	const selectStatus = async (e: any) => {
		if (e.key && id) {
			try {
				setProcessing(true)
				await postReq(`/api/admin/forms/${id}/status`, undefined, { status: e.key })
				setStatus(e.key)
				dispatch({ type: FORM_TEMPLATE.FORM_TEMPLATE_CHANGE_BACKUP_STATUS, status: e.key })
			} catch (err: any) {
				/* eslint-disable-next-line no-console */
				console.error(err.message)
			} finally {
				setProcessing(false)
			}
		}
	}

	const menu = (
		<Menu>
			<Menu.Item
				onClick={() => {
					dispatch(logOutUser())
					message.destroy(ANT_MESSAGE_KEY)
				}}
				key='1'
			>
				<LogOutIcon /> {t('loc:Log out')}
			</Menu.Item>
			<div className={'version mx-3 pt-3'}>
				<div className={'version-info text-gray-500 text-xs tracking-wider'}>{`BE v${BEVersion.originalData}`}</div>
				<div className={'version-divider rounded-full mx-2'} />
				<div className={'version-info text-gray-500 text-xs tracking-wider'}>{`FE v${process.env.REACT_APP_VERSION}`}</div>
				{iFrameVersion?.originalData && (
					<>
						<div className={'version-divider rounded-full mx-2'} />
						<div className={'version-info text-gray-500 text-xs tracking-wider'}>{`iF v${iFrameVersion.originalData}`}</div>
					</>
				)}
			</div>
		</Menu>
	)

	const statuses: MenuProps['items'] = [
		{
			label: t('loc:Production'),
			key: FORM_TEMPLATE_STATUSES.PRODUCTION
		},
		{
			label: t('loc:In progress'),
			key: FORM_TEMPLATE_STATUSES.IN_PROGRESS
		},
		{
			label: t('loc:Archived'),
			key: FORM_TEMPLATE_STATUSES.ARCHIVED
		}
	]

	/* routing to another components inside of form builder */
	const onTabClicked = (key: string) => {
		setActiveKey(key)
		if (key === FORM_BUILDER_HEADER_TABS.BUILDER) {
			navigate(`${t(`paths:/form-builder/${id}`)}${backUrlQuery}`)
		} else if (key === FORM_BUILDER_HEADER_TABS.PREVIEW) {
			navigate(`${t(`paths:/form-preview/${id}`)}${backUrlQuery}`)
		} else if (key === FORM_BUILDER_HEADER_TABS.SETTINGS) {
			navigate(`${t(`paths:/settings/${id}`)}${backUrlQuery}`)
		}
	}

	const backToList = () => {
		// reset form store
		dispatch({ type: RESET_FORM_STORE })
		message.destroy(ANT_MESSAGE_KEY)
		if (backUrlLink) {
			navigate(backUrlLink)
		}
	}

	const editClass = cx({
		'in-progress-select': status === FORM_TEMPLATE_STATUSES.IN_PROGRESS,
		'production-select': status === FORM_TEMPLATE_STATUSES.PRODUCTION,
		'archived-select': status === FORM_TEMPLATE_STATUSES.ARCHIVED
	})

	const parsedStatus = `${status?.charAt(0)?.toUpperCase()}${status.replace('_', ' ').toLocaleLowerCase().slice(1)}`

	const copyToClipboard = (textToClipboard: string) => {
		// noinspection JSIgnoredPromiseFromCall
		navigator.clipboard.writeText(textToClipboard)
		return textToClipboard
	}

	const renderUserRole = (role: any) => {
		if (role === ROLES.ADMINISTRATOR) {
			return t('loc:Admin')
		}
		return role
	}

	return (
		<div className={'form-builder-header'}>
			<div className={'left-box-form-builder-header'}>
				<Dropdown className={'header-user-dropdown-box'} overlay={menu}>
					<div>
						<Row className={'w-full h-full'} justify={'space-between'}>
							<Col span={5}>
								<div className={'w-full h-full grid items-center'}>
									<AdminAvatar className={'m-0 ml-2 w-40px h-40px'} />
								</div>
							</Col>
							<Col span={16} className={'my-2'}>
								<div className={'grid grid-cols-1 gap-2 place-content-stretch user'}>
									<div className={'text-gray-900 font-medium leading-none'}>{loginUserData?.authUser?.data?.fullName}</div>
									<div className={'text-gray-500 font-regular leading-none user-role'}>
										{renderUserRole(loginUserData?.authUser?.data?.role)}
									</div>
								</div>
							</Col>
							<Col span={3}>
								<div className={'grid justify-items-center justify-start'}>
									<ChevronDownIcon className={'ml-auto'} />
								</div>
							</Col>
						</Row>
					</div>
				</Dropdown>
			</div>
			<div className={'middle-box-form-builder-header'}>
				<ul>
					<li className={'middle-box-form-builder-header-label'}>
						<ArrowLeftIcon onClick={backToList} /> <label>{data.formTemplate.data?.backup?.name}</label>
					</li>
					<li className={'middle-box-form-builder-header-nav'}>
						<Tabs onTabClick={onTabClicked} defaultActiveKey={activeKey}>
							<TabPane tab={t('loc:Builder')} key={FORM_BUILDER_HEADER_TABS.BUILDER} />
							<TabPane tab={t('loc:Preview')} key={FORM_BUILDER_HEADER_TABS.PREVIEW} />
							<TabPane tab={t('loc:Settings')} key={FORM_BUILDER_HEADER_TABS.SETTINGS} />
						</Tabs>
					</li>
				</ul>
				<div>
					<Spin className={'header-spinner'} indicator={antIcon} spinning={load} />
					<Dropdown disabled={disable || processing} className={editClass} menu={{ onClick: selectStatus, items: statuses }}>
						<Button>
							{parsedStatus} <ChevronDownIcon />
						</Button>
					</Dropdown>
				</div>
			</div>
			<div className={'right-box-form-builder-header'}>
				<Popover
					overlayInnerStyle={{ padding: '0px', borderRadius: '6px' }}
					trigger='click'
					placement='left'
					content={
						<Tooltip
							placement='bottom'
							title={
								<div>
									<CopyLinkIcon />
									<span className={'ml-2'}>{t('loc:Link copied to clipboard')}</span>
								</div>
							}
						>
							<div className={'p-2'}>{data.formTemplate.data?.iframeUrl}</div>
						</Tooltip>
					}
				>
					<Button
						onClick={() => {
							copyToClipboard(data.formTemplate.data?.iframeUrl || '')
						}}
						className={'unfilled-btn w-127px h-40px'}
					>
						{t('loc:Copy link')}
					</Button>
				</Popover>
				<Button disabled={disable || processing} className={'w-127px h-40px'} type='primary' onClick={publishForm}>
					{t('loc:Publish changes')}
				</Button>
			</div>
		</div>
	)
}

export default FormBuilderHeader
